/**
 * H265 软解码：FLV 解复用 + libde265 解码 + Canvas 渲染。
 * 灰块排查：关键节点已标 [H265-SOFT-N]，出现问题时告知具体节点便于定位。
 */

/* eslint-disable no-console */

const START_CODE = new Uint8Array([0x00, 0x00, 0x00, 0x01]);

function u24be(b: Uint8Array, o: number): number {
    return (b[o]! << 16) | (b[o + 1]! << 8) | b[o + 2]!;
}

function u32be(b: Uint8Array, o: number): number {
    return b[o]! * 2 ** 24 + (b[o + 1]! << 16) + (b[o + 2]! << 8) + b[o + 3]!;
}

function concatU8(chunks: Uint8Array[]): Uint8Array {
    let total = 0;
    for (const c of chunks) total += c.byteLength;
    const out = new Uint8Array(total);
    let p = 0;
    for (const c of chunks) {
        out.set(c, p);
        p += c.byteLength;
    }
    return out;
}

interface HvcCResult {
    lengthSize: number;
    paramSetsAnnexB: Uint8Array[];
}

/**
 * 解析 HEVCDecoderConfigurationRecord
 */
function parseHvcC(hvcc: Uint8Array): HvcCResult | null {
    if (!hvcc || hvcc.byteLength < 23) return null;
    const b = hvcc;
    const lengthSizeMinusOne = (b[21] ?? 0) & 0x03;
    const lengthSize = lengthSizeMinusOne + 1;
    const numOfArrays = b[22] ?? 0;
    let off = 23;
    const paramSets: Uint8Array[] = [];
    for (let i = 0; i < numOfArrays; i++) {
        if (off + 3 > b.byteLength) break;
        const nalUnitType = (b[off] ?? 0) & 0x3f;
        const numNalus = ((b[off + 1] ?? 0) << 8) | (b[off + 2] ?? 0);
        off += 3;
        for (let j = 0; j < numNalus; j++) {
            if (off + 2 > b.byteLength) break;
            const nalLen = ((b[off] ?? 0) << 8) | (b[off + 1] ?? 0);
            off += 2;
            if (off + nalLen > b.byteLength) break;
            const nal = b.subarray(off, off + nalLen);
            off += nalLen;
            // VPS=32, SPS=33, PPS=34
            if (nalUnitType === 32 || nalUnitType === 33 || nalUnitType === 34) {
                paramSets.push(concatU8([START_CODE, nal]));
            }
        }
    }
    return { lengthSize, paramSetsAnnexB: paramSets };
}

export interface DemuxerStats {
    flvHeader: boolean;
    seqHeaders: number;
    tagsTotal: number;
    tagsAudio: number;
    tagsVideo: number;
    tagsScript: number;
    hevcVideoTags: number;
    videoCodecOther: number;
    hevcFrames: number;
    keyframes: number;
    lastTimestamp: number;
    lastTagType: number | null;
    lastVideoCodecId: number | string | null;
    lastVideoPacketType: number | null;
    isEnhanced: boolean;
    pushedToDecoder: number;
    bufBytes?: number;
}

/** FLV Demuxer：传统 FLV(CodecID=12) + Enhanced FLV(hvc1) */
export class FlvHevcDemuxer {
    private onAnnexB: (data: Uint8Array, timestamp: number) => void;
    private buf: Uint8Array = new Uint8Array(0);
    private headerParsed: boolean = false;
    private lengthSize: number = 4;
    private paramSetsAnnexB: Uint8Array[] | null = null;
    public onStats: ((stats: DemuxerStats & { bufBytes: number }) => void) | null = null;
    /** 收到新序列头(VPS/SPS/PPS)时调用，用于软解在直播中途序列切换时重置解码器并推送新参数集 */
    public onSeqHeader: ((annexB: Uint8Array) => void) | null = null;
    public stats: DemuxerStats = {
        flvHeader: false,
        seqHeaders: 0,
        tagsTotal: 0,
        tagsAudio: 0,
        tagsVideo: 0,
        tagsScript: 0,
        hevcVideoTags: 0,
        videoCodecOther: 0,
        hevcFrames: 0,
        keyframes: 0,
        lastTimestamp: 0,
        lastTagType: null,
        lastVideoCodecId: null,
        lastVideoPacketType: null,
        isEnhanced: false,
        pushedToDecoder: 0
    };

    constructor(onAnnexB: (data: Uint8Array, timestamp: number) => void) {
        this.onAnnexB = onAnnexB;
    }

    reset() {
        this.buf = new Uint8Array(0);
        this.headerParsed = false;
        this.lengthSize = 4;
        // 保留 paramSetsAnnexB，避免缓冲重置后直播不再发序列头导致卡死或继续推脏数据
        this.stats = {
            flvHeader: false,
            seqHeaders: 0,
            tagsTotal: 0,
            tagsAudio: 0,
            tagsVideo: 0,
            tagsScript: 0,
            hevcVideoTags: 0,
            videoCodecOther: 0,
            hevcFrames: 0,
            keyframes: 0,
            lastTimestamp: 0,
            lastTagType: null,
            lastVideoCodecId: null,
            lastVideoPacketType: null,
            isEnhanced: false,
            pushedToDecoder: 0
        };
    }

    /** [H265-SOFT-1] FLV 数据入口：网络 chunk 追加到缓冲区并解析 */
    append(chunk: ArrayBuffer | Uint8Array) {
        if (!chunk || (chunk as any).byteLength === 0) return;
        this.buf = concatU8([this.buf, new Uint8Array(chunk)]);
        this._parse();
    }

    private _parse() {
        let p = 0;
        const b = this.buf;

        if (!this.headerParsed) {
            if (b.byteLength < 9) return;
            if (b[0] !== 0x46 || b[1] !== 0x4c || b[2] !== 0x56) {
                console.error("[FLV] Not FLV, first bytes:", b.subarray(0, 16));
                throw new Error("不是 FLV");
            }
            const dataOffset = u32be(b, 5);
            if (b.byteLength < dataOffset + 4) return;
            p = dataOffset + 4;
            this.headerParsed = true;
            this.stats.flvHeader = true;
            console.log("[FLV] Header OK, dataOffset=", dataOffset);
        }

        while (true) {
            if (p + 11 > b.byteLength) break;
            const tagType = b[p] ?? 0;
            const dataSize = u24be(b, p + 1);
            const timestamp = u24be(b, p + 4) + ((b[p + 7] ?? 0) << 24);
            const tagTotal = 11 + dataSize + 4;
            if (p + tagTotal > b.byteLength) break;

            this.stats.tagsTotal++;
            this.stats.lastTagType = tagType;

            if (tagType === 9 && dataSize > 1) {
                this.stats.tagsVideo++;
                const data = b.subarray(p + 11, p + 11 + dataSize);
                /* [H265-SOFT-2] 解析到视频 tag，进入视频处理 */
                this._handleVideoTag(data, timestamp);
            } else if (tagType === 8) {
                this.stats.tagsAudio++;
            } else if (tagType === 18) {
                this.stats.tagsScript++;
            }
            p += tagTotal;

            if (this.onStats && this.stats.tagsTotal % 30 === 0) {
                this.onStats({ ...this.stats, bufBytes: this.buf.byteLength });
            }
        }
        if (p > 0) this.buf = b.subarray(p);
    }

    private _handleVideoTag(data: Uint8Array, timestamp: number) {
        if (data.byteLength < 5) return;

        const firstByte = data[0] ?? 0;
        const frameType = (firstByte >> 4) & 0x07; // bit 6-4
        const isExHeader = (firstByte & 0x80) !== 0; // bit 7

        // Enhanced FLV 检测：isExHeader=1 或 FourCC 匹配
        if (isExHeader) {
            // Enhanced FLV
            const packetType = firstByte & 0x0f;
            const fourCC = String.fromCharCode(data[1] ?? 0, data[2] ?? 0, data[3] ?? 0, data[4] ?? 0);
            this.stats.isEnhanced = true;
            this.stats.lastVideoCodecId = fourCC;
            this.stats.lastVideoPacketType = packetType;

            if (fourCC === "hvc1" || fourCC === "hev1") {
                this._handleEnhancedHevc(data, timestamp, frameType, packetType);
            } else {
                this.stats.videoCodecOther++;
            }
        } else {
            // 传统 FLV
            const codecId = firstByte & 0x0f;
            this.stats.lastVideoCodecId = codecId;

            if (codecId === 12) {
                this._handleTraditionalHevc(data, timestamp, frameType);
            } else {
                this.stats.videoCodecOther++;
            }
        }
    }

    private _handleEnhancedHevc(data: Uint8Array, timestamp: number, frameType: number, packetType: number) {
        this.stats.hevcVideoTags++;
        this.stats.lastTimestamp = timestamp;
        const payload = data.subarray(5);

        if (packetType === 0) {
            const parsed = parseHvcC(payload);
            if (parsed) {
                this.lengthSize = parsed.lengthSize;
                this.paramSetsAnnexB = parsed.paramSetsAnnexB;
                this.stats.seqHeaders++;
                console.log("[H265] demux_seq enhanced", this.paramSetsAnnexB.length, "paramSets");
                if (this.onSeqHeader && this.paramSetsAnnexB.length) {
                    this.onSeqHeader(concatU8(this.paramSetsAnnexB));
                }
            }
            if (this.onStats) this.onStats({ ...this.stats, bufBytes: this.buf.byteLength });
            return;
        }

        if (packetType === 1) {
            // CodedFrames (有 CompositionTime)
            if (payload.byteLength < 3) return;
            const nalPayload = payload.subarray(3);
            this._extractNals(nalPayload, timestamp, frameType);
        } else if (packetType === 3) {
            // CodedFramesX (无 CompositionTime)
            this._extractNals(payload, timestamp, frameType);
        }
    }

    private _handleTraditionalHevc(data: Uint8Array, timestamp: number, frameType: number) {
        const packetType = data[1] ?? 0;
        this.stats.lastVideoPacketType = packetType;
        this.stats.hevcVideoTags++;
        this.stats.lastTimestamp = timestamp;
        const payload = data.subarray(5);

        if (packetType === 0) {
            const parsed = parseHvcC(payload);
            if (parsed) {
                this.lengthSize = parsed.lengthSize;
                this.paramSetsAnnexB = parsed.paramSetsAnnexB;
                this.stats.seqHeaders++;
                console.log("[H265] demux_seq traditional", this.paramSetsAnnexB.length, "paramSets");
                if (this.onSeqHeader && this.paramSetsAnnexB.length) {
                    this.onSeqHeader(concatU8(this.paramSetsAnnexB));
                }
            }
            if (this.onStats) this.onStats({ ...this.stats, bufBytes: this.buf.byteLength });
            return;
        }
        if (packetType !== 1) return;
        this._extractNals(payload, timestamp, frameType);
    }

    private _extractNals(payload: Uint8Array, timestamp: number, frameType: number) {
        if (!payload || payload.byteLength === 0) return;

        if (!this.paramSetsAnnexB || this.paramSetsAnnexB.length === 0) {
            console.log("[H265] demux_skip no paramSets, frameType=", frameType);
            return;
        }

        const chunks: Uint8Array[] = [];

        if (frameType === 1 && this.paramSetsAnnexB && this.paramSetsAnnexB.length) {
            chunks.push(...this.paramSetsAnnexB);
            this.stats.keyframes++;
        }

        // 检测 Annex-B
        if (payload[0] === 0 && payload[1] === 0 && (payload[2] === 1 || (payload[2] === 0 && payload[3] === 1))) {
            chunks.push(payload);
            this.stats.hevcFrames++;
            this.stats.pushedToDecoder++;
            if (this.stats.pushedToDecoder % 60 === 1)
                console.log("[H265] demux_push annexB key=", frameType === 1, "len=", payload.byteLength);
            if (this.onStats) this.onStats({ ...this.stats, bufBytes: this.buf.byteLength });
            this.onAnnexB(concatU8(chunks), timestamp);
            return;
        }

        let off = 0;
        let ls = this.lengthSize || 4;
        if (!this.paramSetsAnnexB) {
            for (const tryLs of [4, 3, 2, 1]) {
                if (payload.byteLength < tryLs + 1) continue;
                let nalLen = 0;
                if (tryLs === 1) nalLen = payload[0] ?? 0;
                else if (tryLs === 2) nalLen = ((payload[0] ?? 0) << 8) | (payload[1] ?? 0);
                else if (tryLs === 3) nalLen = ((payload[0] ?? 0) << 16) | ((payload[1] ?? 0) << 8) | (payload[2] ?? 0);
                else nalLen = u32be(payload, 0);
                if (nalLen > 0 && tryLs + nalLen <= payload.byteLength && ((payload[tryLs] ?? 0) & 0x80) === 0) {
                    ls = tryLs;
                    break;
                }
            }
        }

        while (off + ls <= payload.byteLength) {
            let nalLen = 0;
            if (ls === 1) nalLen = payload[off] ?? 0;
            else if (ls === 2) nalLen = ((payload[off] ?? 0) << 8) | (payload[off + 1] ?? 0);
            else if (ls === 3)
                nalLen = ((payload[off] ?? 0) << 16) | ((payload[off + 1] ?? 0) << 8) | (payload[off + 2] ?? 0);
            else nalLen = u32be(payload, off);
            off += ls;
            if (nalLen <= 0 || off + nalLen > payload.byteLength) break;
            const nal = payload.subarray(off, off + nalLen);
            off += nalLen;
            chunks.push(START_CODE, nal);
        }

        if (chunks.length) {
            this.stats.hevcFrames++;
            this.stats.pushedToDecoder++;
            const annexB = concatU8(chunks);
            if (this.stats.pushedToDecoder % 60 === 1)
                console.log("[H265] demux_push nals key=", frameType === 1, "len=", annexB.byteLength);
            if (this.onStats) this.onStats({ ...this.stats, bufBytes: this.buf.byteLength });
            this.onAnnexB(annexB, timestamp);
        }
    }

    getBufferSize(): number {
        return this.buf.byteLength;
    }
}

export interface H265SoftDecoderOptions {
    canvas: HTMLCanvasElement;
    onStatusChange?: (status: string) => void;
    onError?: (error: Error) => void;
    onPlaying?: () => void;
    onPaused?: () => void;
    isLive?: boolean;
}

/**
 * H265 软解码播放器
 */
export class H265SoftDecoder {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D | null = null;
    private decoder: any = null;
    private demuxer: FlvHevcDemuxer | null = null;
    private abortController: AbortController | null = null;
    private running: boolean = false;
    private paused: boolean = false;
    private resumePromise: Promise<void> | null = null;
    private resumeResolve: (() => void) | null = null;
    private imageData: ImageData | null = null;
    private decodeScheduled: boolean = false;
    private decodePumpTimer: number | null = null;
    private rafId: number | null = null;
    private frames: number = 0;
    private lastFpsTs: number = 0;
    private bytesReceived: number = 0;
    private pendingFrame: any = null;

    private onStatusChange?: (status: string) => void;
    private onError?: (error: Error) => void;
    private onPlaying?: () => void;
    private onPaused?: () => void;
    private playingDispatched: boolean = false;
    private isLive: boolean = false;
    /** 直播时跳过前几帧，避免解码器初始输出灰块 */
    private static readonly LIVE_SKIP_FRAMES = 3;
    /** 直播中途收到新序列头(VPS/SPS/PPS)后跳过的帧数，避免序列切换时灰块 */
    private static readonly SEQ_CHANGE_SKIP_FRAMES = 3;
    private decodedFrameCount: number = 0;
    /** 解码报错后待跳过的帧数（多跳 1～2 帧从源头减少灰帧输出） */
    private skipFramesAfterError: number = 0;
    /** 收到新序列头后待跳过的帧数（>0 时不显示，避免序列切换灰块） */
    private skipFramesAfterSeqChange: number = 0;
    /** 上一组序列头字节，用于判断是否真正切换（避免重复发送相同 VPS/SPS/PPS 时误 reset） */
    private lastSeqHeaderBytes: Uint8Array | null = null;
    /** 解码器是否已就绪（已收到序列参数集），从源头防止灰帧：未就绪时不推送帧数据 */
    private decoderReady: boolean = false;

    constructor(options: H265SoftDecoderOptions) {
        this.canvas = options.canvas;
        this.ctx = this.canvas.getContext("2d", { willReadFrequently: false });
        if (this.ctx) {
            this.ctx.imageSmoothingEnabled = true;
            if ("imageSmoothingQuality" in this.ctx) {
                (this.ctx as CanvasRenderingContext2D & { imageSmoothingQuality: string }).imageSmoothingQuality =
                    "high";
            }
        }
        this.onStatusChange = options.onStatusChange;
        this.onError = options.onError;
        this.onPlaying = options.onPlaying;
        this.onPaused = options.onPaused;
        this.isLive = options.isLive ?? false;
    }

    /**
     * 检查 libde265 是否可用
     */
    static isAvailable(): boolean {
        return typeof window !== "undefined" && !!window.libde265;
    }

    /**
     * 开始播放
     */
    async play(url: string): Promise<void> {
        if (this.running) return;

        if (!H265SoftDecoder.isAvailable()) {
            const error = new Error("libde265 未加载");
            this.onError?.(error);
            throw error;
        }

        this.running = true;
        this.paused = false;
        this.resumePromise = null;
        this.resumeResolve = null;
        this.playingDispatched = false;
        this.frames = 0;
        this.decodedFrameCount = 0;
        this.skipFramesAfterError = 0;
        this.skipFramesAfterSeqChange = 0;
        this.decoderReady = false;
        this.lastSeqHeaderBytes = null;
        this.lastFpsTs = performance.now();
        this.bytesReceived = 0;

        this.playingDispatched = true;
        this.onPlaying?.();

        this.decoder = new window.libde265.Decoder();
        this.decoder.disable_filters(false);
        console.log("[H265] decoder_created");

        this.decoder.set_image_callback((img: any) => {
            this.frames++;
            this.decodedFrameCount++;
            if (this.isLive && this.decodedFrameCount <= H265SoftDecoder.LIVE_SKIP_FRAMES) {
                console.log("[H265] frame_skip_live n=", this.decodedFrameCount);
                img.free();
                return;
            }
            if (this.skipFramesAfterSeqChange > 0) {
                this.skipFramesAfterSeqChange--;
                console.log("[H265] frame_skip_seq_change remaining=", this.skipFramesAfterSeqChange);
                img.free();
                return;
            }
            if (this.skipFramesAfterError > 0) {
                this.skipFramesAfterError--;
                console.log("[H265] frame_skip_err remaining=", this.skipFramesAfterError);
                img.free();
                return;
            }
            if (this.pendingFrame) this.pendingFrame.free();
            this.pendingFrame = img;
            const w = img.get_width();
            const h = img.get_height();
            if (this.frames % 60 === 1) console.log("[H265] frame_pending n=", this.frames, "size=", w, "x", h);
        });

        this.demuxer = new FlvHevcDemuxer((annexB, ts) => {
            if (!this.decoder) {
                console.log("[H265] demux_push_skip no decoder");
                return;
            }
            // 从源头防止灰帧：解码器未就绪（未收到序列参数集）时不推送帧数据
            if (!this.decoderReady) {
                console.log("[H265] demux_push_skip decoder not ready (no seq header yet)");
                return;
            }
            console.log("[H265] demux_push_data len=", annexB.byteLength, "ts=", ts);
            this.decoder.push_data(annexB, ts || 0);
            this.scheduleDecode();
        });
        this.demuxer.onSeqHeader = annexB => {
            if (!this.decoder) return;
            const isMidStream = (this.demuxer?.stats.seqHeaders ?? 0) > 1;
            const paramSetsChanged =
                !this.lastSeqHeaderBytes ||
                this.lastSeqHeaderBytes.byteLength !== annexB.byteLength ||
                !this.lastSeqHeaderBytes.every((b, i) => b === annexB[i]);
            this.lastSeqHeaderBytes = annexB.slice(0);

            if (isMidStream && paramSetsChanged) {
                // 重置解码器并清空就绪状态，等待新序列参数集推送完成后再允许帧数据
                this.decoder.reset?.();
                this.decoderReady = false;
                if (this.pendingFrame) {
                    try {
                        this.pendingFrame.free();
                    } catch (_) {}
                    this.pendingFrame = null;
                }
                this.skipFramesAfterSeqChange = H265SoftDecoder.SEQ_CHANGE_SKIP_FRAMES;
                console.log(
                    "[H265] seq_header mid-stream (params changed), reset decoder, skip next",
                    H265SoftDecoder.SEQ_CHANGE_SKIP_FRAMES,
                    "frames"
                );
            } else if (isMidStream && !paramSetsChanged) {
                console.log("[H265] seq_header mid-stream (same params), no reset");
            }
            // 先推送序列参数集，确保解码器就绪
            this.decoder.push_data(annexB, 0);
            this.decoderReady = true;
            console.log("[H265] seq_header pushed, decoder ready");
            this.scheduleDecode();
        };

        this.demuxer.onStats = s => {
            if (!this.running) return;
            if (!this.playingDispatched && (s.hevcFrames > 0 || s.pushedToDecoder > 0)) {
                this.playingDispatched = true;
                this.onPlaying?.();
            }
            if (this.frames === 0) {
                let msg = `解析中... bytes=${this.bytesReceived} buf=${s.bufBytes || 0}`;
                msg += ` | tags=${s.tagsTotal} (v=${s.tagsVideo}/a=${s.tagsAudio}/s=${s.tagsScript})`;
                msg += ` | enhanced=${s.isEnhanced} codecId=${s.lastVideoCodecId || "-"} pkt=${s.lastVideoPacketType || "-"}`;
                msg += ` | hevcTags=${s.hevcVideoTags} seqHeaders=${s.seqHeaders} keyframes=${s.keyframes}`;
                msg += ` | hevcFrames=${s.hevcFrames} pushed=${s.pushedToDecoder}`;
                this.onStatusChange?.(msg);
            }
        };

        this.startRenderLoop();

        const parent = this.canvas.parentElement;
        if (parent && typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.videoWidth > 0 && this.videoHeight > 0) this.updateCanvasLayout();
            });
            this.resizeObserver.observe(parent);
        }

        this.decodePumpTimer = window.setInterval(() => {
            if (!this.running || !this.decoder || this.paused) return;
            for (let i = 0; i < 12; i++) {
                this.scheduleDecode();
            }
        }, 5);

        this.abortController = new AbortController();
        this.onStatusChange?.("正在连接...");

        try {
            const res = await fetch(url, {
                signal: this.abortController.signal,
                mode: "cors",
                cache: "no-store"
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            if (!res.body) throw new Error("不支持 ReadableStream");

            this.onStatusChange?.("已连接，拉流中...");
            const reader = res.body.getReader();

            while (this.running) {
                if (this.paused) {
                    await this.waitForResume();
                    continue;
                }
                const { value, done } = await reader.read();
                if (done) break;
                if (value) {
                    this.bytesReceived += value.byteLength;

                    if (this.demuxer && this.demuxer.getBufferSize() > 8 * 1024 * 1024) {
                        console.log("[H265] buffer_reset size>8MB");
                        this.demuxer.reset();
                        this.decoder?.reset?.();
                        this.lastSeqHeaderBytes = null;
                        this.decoderReady = false;
                        this.onStatusChange?.(`缓冲过大，已丢弃... bytes=${this.bytesReceived}`);
                    }

                    this.demuxer?.append(value);
                }
            }
            this.onStatusChange?.("流已结束");
        } catch (e: any) {
            if (String(e).includes("AbortError")) {
                this.onStatusChange?.("已停止");
            } else {
                this.onStatusChange?.(`拉流失败: ${String(e)}`);
                this.onError?.(e);
            }
        } finally {
            this.stop(false);
        }
    }

    /**
     * 停止播放
     */
    stop(updateStatus: boolean = true): void {
        this.running = false;
        this.paused = false;
        this.signalResume();
        this.onPaused?.();

        if (this.resizeObserver && this.canvas.parentElement) {
            this.resizeObserver.unobserve(this.canvas.parentElement);
            this.resizeObserver = null;
        }

        try {
            this.abortController?.abort();
        } catch (_) {}
        this.abortController = null;

        if (this.decodePumpTimer) {
            clearInterval(this.decodePumpTimer);
            this.decodePumpTimer = null;
        }

        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        this.demuxer?.reset();
        this.demuxer = null;
        this.imageData = null;
        this.decoderReady = false;
        this.lastSeqHeaderBytes = null;

        if (this.pendingFrame) {
            try {
                this.pendingFrame.free();
            } catch (_) {}
            this.pendingFrame = null;
        }

        if (this.decoder) {
            try {
                this.decoder.free();
            } catch (_) {}
            this.decoder = null;
        }

        if (updateStatus) {
            this.onStatusChange?.("已停止");
        }
    }

    /**
     * 是否正在运行
     */
    isRunning(): boolean {
        return this.running;
    }

    /**
     * 是否暂停中
     */
    isPaused(): boolean {
        return this.paused;
    }

    /**
     * 暂停（保留解码/网络状态）
     */
    pause(): void {
        if (!this.running || this.paused) return;
        this.paused = true;
        this.onPaused?.();
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    /**
     * 继续播放
     */
    resume(): void {
        if (!this.running || !this.paused) return;
        this.paused = false;
        this.onPlaying?.();
        if (!this.rafId) this.startRenderLoop();
        this.signalResume();
    }

    /** 单次连续解码的最大帧数，避免长时间阻塞主线程 */
    private static readonly MAX_DECODE_PER_SCHEDULE = 4;

    private scheduleDecode(): void {
        if (!this.decoder || this.decodeScheduled || this.paused) return;
        this.decodeScheduled = true;

        const decodeNext = (count: number) => {
            if (!this.decoder || !this.running) {
                this.decodeScheduled = false;
                return;
            }
            this.decoder.decode((err: number) => {
                if (err === window.libde265.DE265_ERROR_WAITING_FOR_INPUT_DATA) {
                    this.decodeScheduled = false;
                    return;
                }
                if (!window.libde265.de265_isOK(err)) {
                    console.log("[H265] decode_err", window.libde265.de265_get_error_text(err), "errCode=", err);
                    if (err !== window.libde265.DE265_ERROR_WAITING_FOR_INPUT_DATA) {
                        this.skipFramesAfterError = 2;
                        // 连续解码错误可能是参数集丢失或损坏，重置就绪状态，强制等待新的序列参数集
                        // 这样可以避免在参数集不完整时继续解码产生灰帧
                        if (this.decoderReady) {
                            console.log("[H265] decode_err reset decoder ready state, waiting for new seq header");
                            this.decoderReady = false;
                        }
                    }
                    this.decodeScheduled = false;
                    return;
                }
                console.log("[H265] decode_ok count=", count);
                if (this.decoder && this.decoder.has_more() && count < H265SoftDecoder.MAX_DECODE_PER_SCHEDULE) {
                    decodeNext(count + 1);
                } else {
                    this.decodeScheduled = false;
                }
            });
        };

        setTimeout(() => decodeNext(0), 0);
    }

    /** 当前视频宽高，用于保持比例布局 */
    private videoWidth: number = 0;
    private videoHeight: number = 0;
    private resizeObserver: ResizeObserver | null = null;
    private renderDisplayCount: number = 0;

    /**
     * 按容器尺寸和视频比例设置 canvas 显示区域，保持原始比例不拉伸
     */
    private updateCanvasLayout(): void {
        const parent = this.canvas.parentElement;
        if (!parent || this.videoWidth <= 0 || this.videoHeight <= 0) return;

        const cw = parent.clientWidth;
        const ch = parent.clientHeight;
        if (cw <= 0 || ch <= 0) return;

        const scale = Math.min(cw / this.videoWidth, ch / this.videoHeight);
        const displayW = Math.round(this.videoWidth * scale);
        const displayH = Math.round(this.videoHeight * scale);
        const left = Math.round((cw - displayW) / 2);
        const top = Math.round((ch - displayH) / 2);

        this.canvas.style.width = `${displayW}px`;
        this.canvas.style.height = `${displayH}px`;
        this.canvas.style.left = `${left}px`;
        this.canvas.style.top = `${top}px`;
    }

    private startRenderLoop(): void {
        const renderLoop = () => {
            if (!this.running) return;
            if (this.paused) return;

            // 渲染待处理的帧
            if (this.pendingFrame) {
                const img = this.pendingFrame;
                this.pendingFrame = null;
                const w = img.get_width();
                const h = img.get_height();
                console.log("[H265] render_start w=", w, "h=", h);

                if (this.canvas.width !== w || this.canvas.height !== h || !this.imageData) {
                    this.canvas.width = w;
                    this.canvas.height = h;
                    this.videoWidth = w;
                    this.videoHeight = h;
                    this.imageData = this.ctx!.createImageData(w, h);
                    this.updateCanvasLayout();
                    console.log("[H265] render_resize", w, "x", h);
                }

                img.display(this.imageData, (rgb: ImageData) => {
                    if (!rgb || rgb.data.length === 0) {
                        console.log("[H265] render_skip empty rgb");
                        return;
                    }
                    this.ctx!.putImageData(rgb, 0, 0);
                    this.renderDisplayCount++;
                    console.log("[H265] render_show", w, "x", h, "rgbLen=", rgb.data.length);
                });
                img.free();
            }

            // 更新 fps 统计
            const now = performance.now();
            if (now - this.lastFpsTs >= 1000) {
                const fps = (this.frames * 1000) / (now - this.lastFpsTs);
                this.onStatusChange?.(`正在播放... ${fps.toFixed(1)} fps | bytes=${this.bytesReceived}`);
                this.frames = 0;
                this.lastFpsTs = now;
            }

            this.rafId = requestAnimationFrame(renderLoop);
        };

        this.rafId = requestAnimationFrame(renderLoop);
    }

    private waitForResume(): Promise<void> {
        if (!this.resumePromise) {
            this.resumePromise = new Promise(resolve => {
                this.resumeResolve = resolve;
            });
        }
        return this.resumePromise;
    }

    private signalResume(): void {
        if (this.resumeResolve) {
            this.resumeResolve();
        }
        this.resumePromise = null;
        this.resumeResolve = null;
    }

    /**
     * 销毁播放器
     */
    destroy(): void {
        this.stop(false);
    }
}

/**
 * 检查浏览器是否支持 H265 硬解码
 */
export async function checkH265HardwareSupport(): Promise<boolean> {
    // 检查 MSE 是否支持
    if (!("MediaSource" in window)) {
        return false;
    }

    // 检查是否支持 HEVC codec
    const mimeTypes = [
        'video/mp4; codecs="hvc1.1.6.L93.B0"',
        'video/mp4; codecs="hev1.1.6.L93.B0"',
        'video/mp4; codecs="hvc1"',
        'video/mp4; codecs="hev1"'
    ];

    for (const mimeType of mimeTypes) {
        if (MediaSource.isTypeSupported(mimeType)) {
            return true;
        }
    }

    return false;
}

export default H265SoftDecoder;
